import { DocsItem } from "@academy/components"

const link = (href: string, link: string) =>
  `<a href="${href}" class="underline text-blue-600" target="_blank">${link}</a>`

export const POLICY_STATIC: DocsItem[] = [
  {
    title: "",
    content: [
      {
        html: `This Privacy Policy explains how Ed Technologies Corp. (“Ed Technologies,” “we,” “us,” or “our”) collects, uses, and discloses personal information through the websites and services described below (collectively, the “Services”):`,
      },
      {
        html: `<ul class="list-inside list-disc mb-[1rem]">
          <li class="docs__item mb-[1rem]">KidEntrepreneurs.academy or Kidpreneurs.academy — educational classes and programs;</li>
          <li class="docs__item mb-[1rem]">KidsCrafts.shop — an educational marketplace where children, with verified parental or guardian consent and supervision, can learn business skills and create product listings;</li>
          <li class="docs__item mb-[1rem]">OnlineSafetyReadiness.com — online safety education, training, assessments, and recommendations for individuals, schools, and educational organizations; and</li>
          <li class="docs__item">AfterSchoolVote.com — a platform for parents and guardians to vote on educational courses or programs they would like offered at their children’s schools.</li>
        </ul>`,
      },
    ],
  },
  {
    title: "1. Information We Collect",
    content: [],
  },
  {
    title: "Parents, guardians, and adult users",
    content: [
      {
        list: {
          description: "",
          items: [
            "Name and last name;",
            "Email address;",
            "Phone number;",
            "Mailing or billing address;",
            "Account information;",
            "Information submitted through forms or communications; and",
            "Information needed to process purchases or provide our Services.",
          ],
        },
      },
    ],
  },
  {
    title: "Children and students",
    content: [
      {
        list: {
          description: "",
          items: [
            "On KidEntrepreneurs.academy, OnlineSafetyReadiness.com, and KidsCrafts.shop: during the purchase process, we collect the first name(s) of the child(ren) who will be attending purchased classes.",
            "On KidsCrafts.shop, the seller account itself is created and owned by the parent or guardian (see Section 5.2), using the email address and payment information they provided when enrolling in a program. Information associated with that account may include: a username; a store name that must not contain the child’s full name; user-generated content (product listings, images, descriptions, parameters, and prices submitted through the platform’s monitored systems); transaction data (details of sales, purchases, and, if applicable, shipping addresses used for order fulfillment); and, where a listing is enabled for real purchase, identity verification status from our payment processor (Stripe) confirming the account belongs to a verified adult — Stripe processes the underlying verification documents under its own privacy terms, and we receive confirmation of verification status rather than the documents themselves.",
          ],
        },
      },
    ],
  },
  {
    title: "AfterSchoolVote.com",
    content: [
      {
        html: `We may collect information needed to administer a parent or guardian preference survey, such as an email address, school or district affiliation, voting status, and selected preferences, to prevent duplicate submissions, verify eligibility where applicable, analyze interest in proposed programs, and provide aggregated results to participating schools or organizations. Individual responses are not publicly displayed. We may publish or share aggregated results not reasonably linked to an identifiable parent or guardian.`,
      },
    ],
  },
  {
    title: "Automatically collected information",
    content: [
      {
        html: `We may automatically collect IP address, browser and device information, pages visited, usage and diagnostic information, and similar technical data. See Section 2 for limits that apply on children’s interfaces.`,
      },
    ],
  },
  {
    title: "2. Cookies and Automatic Collection on Children’s Interfaces",
    content: [
      {
        html: `We use cookies and similar technologies to operate our Services, maintain security, understand website usage, remember preferences, and improve our platforms. You can control cookies through your browser settings, and where required by law we request consent before using non-essential cookies.`,
      },
      {
        html: `On pages of KidsCrafts.shop, we limit our use of cookies and similar technologies to what is reasonably necessary for the platform’s internal operations — for example, maintaining and analyzing site functionality, authenticating the logged-in account, security, legal compliance, and preventing repeat actions such as duplicate submissions. We do not use these technologies on Child User interfaces to build behavioral profiles for advertising or to serve targeted advertising to children.`,
      },
    ],
  },
  {
    title: "3. How We Use Information",
    content: [
      {
        html: `<ul class="list-inside list-disc mb-[1rem]">
          <li class="docs__item mb-[1rem]"><strong>To Fulfill Your Requests:</strong> to process class registrations and purchases, manage accounts, operate the KidsCrafts.shop marketplace, administer AfterSchoolVote.com voting, and provide our educational Services.</li>
          <li class="docs__item mb-[1rem]"><strong>To Communicate with You:</strong> to send transactional and service-related communications, respond to inquiries, and, with consent where required, provide information about programs and opportunities.</li>
          <li class="docs__item mb-[1rem]"><strong>For Safety and Moderation:</strong> to monitor and moderate marketplace activity, screen listings for personal information or prohibited content before publication, review user-generated content and transactions, and maintain a safe and appropriate environment.</li>
          <li class="docs__item mb-[1rem]"><strong>To Improve Our Services:</strong> to analyze website and platform usage, understand parent preferences, and improve our educational programs, assessments, websites, and user experience.</li>
          <li class="docs__item"><strong>For Security and Compliance:</strong> to protect the safety and security of our Services, prevent fraud or unauthorized activity, and comply with applicable legal obligations.</li>
        </ul>`,
      },
    ],
  },
  {
    title: "4. Legal Basis for Processing",
    content: [
      {
        html: `Depending on the applicable jurisdiction, we may process personal information based on: (a) <strong>Contract</strong>, when processing is necessary to provide requested Services or fulfill an agreement; (b) <strong>Consent</strong>, where required by law, including verifiable parental consent described in Section 5; (c) <strong>Legitimate interests</strong>, such as improving Services, maintaining security, preventing abuse, and protecting users; and (d) <strong>Legal obligations</strong>, when processing is required by applicable law.`,
      },
    ],
  },
  {
    title: "5. Children’s Privacy and COPPA Compliance",
    content: [
      {
        html: `Some of our Services are designed for or used by children and students. We collect only information reasonably necessary to provide the applicable educational Service. We do not sell children’s personal information and do not use it for behavioral or targeted advertising. We do not condition a child’s participation in an activity on disclosing more personal information than is reasonably necessary to participate.`,
      },
    ],
  },
  {
    title: "5.1 How We Obtain Consent — No Separate Form Required",
    content: [
      {
        html: `We do not ask parents or guardians to complete a separate consent form for KidsCrafts.shop. Instead, consent is built directly into how an account is created:`,
      },
      {
        list: {
          description: "",
          items: [
            "When a parent or legal guardian (an “Authorized Adult”) enrolls a child in an Ed Technologies program, they pay using their own payment method and provide their own email address.",
            "After that purchase is completed, we send an invitation to create a KidsCrafts.shop seller account to that same email address. Only the Authorized Adult who enrolled and paid for the program receives this invitation and can create the account — a child cannot create or hold a KidsCrafts.shop account directly.",
            "The invitation and the account-creation screens serve as our direct notice: they describe what information will be collected through the account (Section 1), how it will be used, and that approved listings may be visible to other users or the public (Section 10).",
            "By completing account creation after receiving that notice, the Authorized Adult gives verifiable consent to the collection, use, and applicable public display of information described in this Privacy Policy, consistent with the U.S. Children’s Online Privacy Protection Act (COPPA), which recognizes verification through a payment method connected to a transaction on the account as an acceptable method of verifiable parental consent.",
          ],
        },
      },
    ],
  },
  {
    title: "5.2 Additional Identity Verification for Purchasable Listings",
    content: [
      {
        html: `Before a listing can be made available for actual purchase by other users, the Authorized Adult’s account must separately complete identity verification through our payment processor (currently Stripe), confirming the account belongs to a verified adult. This is in addition to, not instead of, the consent obtained at account creation described in Section 5.1.`,
      },
    ],
  },
  {
    title: "5.3 Simulation Mode",
    content: [
      {
        html: `KidsCrafts.shop also offers a Simulation Mode that does not require Stripe identity verification. In Simulation Mode, the same account-creation and consent process described in Section 5.1 applies, and a listing is additionally reviewed and must be approved by our support team before it is published. Simulation Mode listings can never be purchased.`,
      },
    ],
  },
  {
    title: "5.4 Scope of Consent",
    content: [
      {
        html: `Consent obtained at account creation covers taking pictures or videos of classes as well as the specific collection, use, and disclosure practices described in the notice provided at that time, including whether a child’s store name and listings will be publicly visible. If we intend to materially change how we collect, use, or disclose a child’s personal information, we will provide new direct notice and obtain renewed consent before doing so.`,
      },
    ],
  },
  {
    title: "5.5 Parental Rights",
    content: [
      {
        html: `A parent or legal guardian may, at any time, contact us to: review the personal information collected from their child; request correction or deletion of that information; refuse to permit further collection or use of their child’s information; and withdraw consent previously given. Upon a request to withdraw consent or delete information, we will deactivate or delete the child’s account and associated personal information, subject to any retention we are legally required or permitted to maintain (see Section 12).`,
      },
    ],
  },
  {
    title: "5.6 AfterSchoolVote.com",
    content: [
      {
        html: `AfterSchoolVote.com is designed for parents and guardians. Children are not intended to create accounts or submit votes on this Service.`,
      },
    ],
  },
  {
    title: "5.7 Canadian Children’s Privacy",
    content: [
      {
        html: `Where applicable, we also take reasonable steps to obtain meaningful consent appropriate to a child’s age and understanding under applicable Canadian privacy laws, in addition to the COPPA measures described above.`,
      },
    ],
  },
  {
    title: "6. School and Student Information",
    content: [
      {
        html: `When our Services are provided through a school, school district, or other educational organization, we may process student information on behalf of that organization. Where applicable, Ed Technologies may act as a service provider or “school official” and process student information only for authorized educational purposes. We intend to handle applicable student information in accordance with FERPA and other applicable state and provincial student privacy laws. We make a data processing agreement available to schools and districts on request. Requests concerning educational records may need to be directed to the relevant school or district.`,
      },
    ],
  },
  {
    title: "7. Canadian Privacy Laws",
    content: [
      {
        html: `For Canadian users, we comply with applicable Canadian privacy legislation, including PIPEDA and, where applicable, the Personal Information Protection Act (PIPA) of British Columbia. We collect, use, and disclose personal information for reasonable purposes related to providing our Services, maintaining security, improving our platforms, administering programs and voting activities, and complying with legal obligations. Subject to applicable law, individuals may request access to or correction of their personal information.`,
      },
    ],
  },
  {
    title: "8. Artificial Intelligence",
    content: [
      {
        html: `Some Services, including OnlineSafetyReadiness.com, use AI-assisted technologies to analyze assessments and provide educational insights or recommendations. AI-generated recommendations are intended to support learning and do not replace the judgment of parents, educators, or qualified professionals. We do not knowingly use children’s personal information to train general-purpose AI models for unrelated purposes, and we do not use assessment data to improve our own Service-specific models beyond what is covered by the consent described in Section 5, unless we provide additional notice.`,
      },
    ],
  },
  {
    title: "9. Sharing Information",
    content: [
      {
        html: `We do not sell or rent personal information. We may share information with:`,
      },
      {
        list: {
          description: "",
          items: [
            "Service providers that help us operate our Services, including hosting, payment processing, security, analytics, communications, and technical providers;",
            "Schools, districts, educators, parents, guardians, or authorized organizations, where appropriate;",
            "Government authorities or other parties, when required or permitted by law or necessary to protect our users, Services, or legal rights;",
            "Business transaction parties, in connection with a merger, acquisition, restructuring, financing, or sale of assets; and",
            "Our other Services, but only for security, fraud prevention, legal compliance, or another purpose consistent with the consent provided for that child’s data; we do not repurpose a Child User’s personal information collected on one Service for a new, unrelated purpose on another Service without additional parental consent.",
          ],
        },
      },
      {
        html: `Service providers are expected to use personal information only as necessary to provide services to us and in accordance with applicable contractual or legal requirements.`,
      },
    ],
  },
  {
    title: "10. KidsCrafts.shop Public Content",
    content: [
      {
        html: `Approved product listings and related marketplace content may be visible to other users or the public when necessary to operate KidsCrafts.shop. Such content may include product images, descriptions, prices, and store information. In addition to parental review, we screen listings using automated filters and, where warranted, human moderators to identify and remove personal information or prohibited content before publication. Parents and guardians should also ensure that a child does not include a full name, address, phone number, email address, school name, or other unnecessary personal information in publicly visible content.`,
      },
    ],
  },
  {
    title: "11. Photo and Video Consent",
    content: [
      {
        html: `With the separate consent of a parent or legal guardian, Ed Technologies Corp. may photograph and/or record children during classes, programs, events, and related activities. Photos and videos may be used for educational, promotional, and informational purposes, including publication on our websites and social media channels. We do not intentionally publish a child’s full name, home address, contact information, or other unnecessary personal information together with photographs or videos without additional consent. Photo and video consent is voluntary, is separate from consent to use KidsCrafts.shop, and is not required for participation in our programs. Parents or legal guardians may withdraw this consent at any time by contacting us.`,
      },
    ],
  },
  {
    title: "12. Security and Data Retention",
    content: [
      {
        html: `We use reasonable technical, administrative, and organizational safeguards designed to protect personal information. No electronic transmission or storage system is completely secure, and we cannot guarantee absolute security.`,
      },
      {
        html: `We retain a Child User’s personal information only for as long as the child’s account remains active, plus a limited period after account closure or a parental deletion request — whichever occurs first — to allow for account recovery and to resolve any pending transactions or disputes, unless a longer period is required by law (for example, financial or tax recordkeeping obligations relating to completed transactions). We retain other personal information only for as long as reasonably necessary to provide our Services, administer accounts and voting activities, comply with legal or contractual obligations, resolve disputes, prevent abuse, and protect our legal rights. When information is no longer required, we delete, anonymize, or otherwise dispose of it in accordance with applicable law.`,
      },
    ],
  },
  {
    title: "13. International Data Transfers",
    content: [
      {
        html: `Our Services may be operated or supported from Canada, the United States, and other countries. Personal information may therefore be transferred to or accessed from jurisdictions outside your country of residence. Where required, we use appropriate safeguards for international transfers in accordance with applicable law, which may include contractual protections with the recipient.`,
      },
    ],
  },
  {
    title: "14. Your Privacy Rights",
    content: [
      {
        html: `Depending on your location and applicable law, you may have the right to: access your personal information; request a copy of your information; correct inaccurate or incomplete information; request deletion of your information; object to or restrict certain processing; and withdraw consent where processing is based on consent. Additional rights may apply under specific privacy laws, including the right to lodge a complaint with your local data protection or privacy supervisory authority.`,
      },
      {
        html: `We do not sell personal information and do not “share” personal information for cross-context behavioral advertising as defined under the California Consumer Privacy Act, as amended by the California Privacy Rights Act. Consistent with that law, we do not sell or share the personal information of a known minor under 16 without opt-in consent. Requests concerning student educational records may need to be made through the relevant school or district.`,
      },
    ],
  },
  {
    title: "15. Third-Party Websites",
    content: [
      {
        html: `Our Services may contain links to third-party websites or services. We are not responsible for their privacy practices and encourage you to review their privacy policies before providing personal information.`,
      },
    ],
  },
  {
    title: "16. Changes to This Privacy Policy",
    content: [
      {
        html: `We may update this Privacy Policy from time to time. Any changes will be posted on our Sites. Where a change materially affects information collected from a child under a parent’s existing consent, we will provide direct notice to the parent and, where required by law, obtain renewed consent. We encourage you to review this Privacy Policy periodically.`,
      },
    ],
  },
  {
    title: "17. Contact Us",
    content: [
      {
        html: `If you have questions about this Privacy Policy, your personal information, your child’s information, or wish to exercise a privacy right or withdraw consent, please contact us:`,
      },
      {
        html: `Attn: Ed Technologies Corp.<br /><br />
        Email: <a class="underline text-primary" href="mailto:service@KidEntrepreneurs.academy">service@KidEntrepreneurs.academy</a>`,
      },
    ],
  },
]
